/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import {
	ColorListFactory,
	LocationListFactory,
	RoleListFactory,
} from 'src/app/common/fancy-firebase/services/factories';
import { FancyIdJoin } from 'src/app/common/fancy-firebase/base/FancyIdJoin';
import { RolesApi } from 'src/app/app-data/state/RolesApi';
import { LocationsApi } from 'src/app/app-data/state/LocationsApi';
import { ColorsApi } from 'src/app/app-data/state/ColorsApi';
import { ColorType } from 'src/app/app-data/constants/color-type.enum';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';

import firebase from 'firebase/compat/app';

@Injectable({
	providedIn: 'root',
})
export class FancyFirebaseColorsRolesLocationsApiBuilder {
	constructor(
		private roleListFactory: RoleListFactory,
		private locationListFactory: LocationListFactory,
		private colorListFactory: ColorListFactory
	) {}

	public getColorsApi(projectId: string, colorFor: ColorType): ColorsApi {
		const projectPath = fancyFirebaseGetters.getProjectsPath(projectId);
		const colorList = this.colorListFactory.create(
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(projectPath, projectId)
				.child(`colors/${colorFor}`)
		);
		const colorsApi = new ColorsApi(colorList);
		return colorsApi;
	}

	public getRolesApi(projectId: string, colorApi: ColorsApi): RolesApi {
		const roleColorJoiner = new FancyIdJoin('roleColor', colorApi.data);
		const roleListPath = fancyFirebaseGetters.getRolesPath(projectId);
		const roleList = this.roleListFactory.create(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(roleListPath, projectId),
			roleColorJoiner
		);
		const rolesApi = new RolesApi(roleList);

		rolesApi.addDestroyer(() => roleColorJoiner.destroy());

		return rolesApi;
	}

	public getLocationsApi(projectId: string, colorApi: ColorsApi): LocationsApi {
		const locationColorJoiner = new FancyIdJoin('locationColor', colorApi.data);
		const locationListPath = fancyFirebaseGetters.getLocationsPath(projectId);
		const locationList = this.locationListFactory.create(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(locationListPath, projectId),
			locationColorJoiner
		);

		const locationsApi = new LocationsApi(locationList);
		locationsApi.addDestroyer(() => locationColorJoiner.destroy());

		return locationsApi;
	}
}
